import React, {useContext} from 'react';

import {
  Button,
  constants,
  useKeyUp,
  useMergedRefs,
  useWindowSize,
} from '@newsela/angelou';
import {css} from 'emotion';
import PropTypes from 'prop-types';
import {CSSTransition} from 'react-transition-group';

import useOnClickOff from '../../../../../utils/useOnClickOff';
import {animationTimeout} from '../../constants';
import {GlobalLayoutRefsContext} from '../../GlobalLayoutRefsContext';
import {
  cancelButton,
  cancelText,
  childrenContainer,
  closeOverlay,
  enter,
  enterActive,
  exit,
  exitActive,
  popoutClassName,
} from './style';

const propTypes = {
  // Pass a string to add more class names
  additionalClassName: PropTypes.string,

  // Pass a string to add more class names to the child container
  additionalChildrenContainerClassName: PropTypes.string,

  // Elements to be rendered inside the drawer
  children: PropTypes.node,

  // Function passed down the cancel <Button> onClick prop
  onCancel: PropTypes.func,

  // Pass onClickOff a function to be triggered when user
  // clicks anywhere outside of the drawer
  onClickOff: PropTypes.func,

  // Pass onEscape a function to be triggered when user hits the escape key
  onEscape: PropTypes.func,

  // Determines styling for the content drawer anchored to the search form.
  isSearchContentDrawer: PropTypes.bool,
};

export const GlobalNavContentDrawer = React.forwardRef((props, ref) => {
  GlobalNavContentDrawer.displayName = 'GlobalNavContentDrawer';

  const {globalNavHeight, pageNavMobileHeight} = useContext(
    GlobalLayoutRefsContext
  );
  const {width: windowWidth} = useWindowSize();
  const {
    breakpoints: {Breakpoints},
  } = constants;

  const {
    additionalClassName,
    additionalChildrenContainerClassName,
    children,
    onCancel,
    onClickOff,
    onEscape,
    isSearchContentDrawer,
  } = props;

  useKeyUp('escape', () => {
    onEscape();
  });
  const clickOffRef = useOnClickOff(onClickOff);
  const mergedRef = ref ? useMergedRefs(ref, clickOffRef) : clickOffRef;

  return (
    <div
      className={css([
        popoutClassName(globalNavHeight, pageNavMobileHeight),
        additionalClassName || null,
      ])}
      ref={mergedRef}
    >
      <div
        className={css([
          childrenContainer(isSearchContentDrawer),
          additionalChildrenContainerClassName || null,
        ])}
      >
        {children}
      </div>
      {windowWidth > Breakpoints.sm ? null : (
        <div className={css(closeOverlay)}>
          <Button
            __classNameFor={{
              root: css(cancelButton),
            }}
            flavor={Button.flavor.incognito}
            onClick={onCancel}
          >
            <p className={css(cancelText)}>Cancel</p>
          </Button>
        </div>
      )}
    </div>
  );
});

GlobalNavContentDrawer.propTypes = propTypes;
GlobalNavContentDrawer.defaultProps = {
  isSearchContentDrawer: false,
};

/**
 * We wrap the globalNavContentDrawer in a cssTransition so that we can
 * safely mount and unmount the component cleaning up the listeners we
 * attach, while still having a nice animation.
 */
const AnimatedGlobalNavContentDrawer = React.forwardRef((props, ref) => {
  AnimatedGlobalNavContentDrawer.displayName = 'AnimatedGlobalNavContentDrawer';

  return (
    <CSSTransition
      in={props.show}
      unmountOnExit
      timeout={animationTimeout}
      classNames={{
        enter: css(enter),
        enterActive: css(enterActive),
        exit: css(exit),
        exitActive: css(exitActive),
      }}
    >
      <GlobalNavContentDrawer {...props} ref={ref} />
    </CSSTransition>
  );
});

AnimatedGlobalNavContentDrawer.propTypes = {
  ...propTypes,
  show: PropTypes.bool,
};

export default AnimatedGlobalNavContentDrawer;
